<template>
    <div :id="rootElementId" :style="`height:${svgHeight}px`" class="polar-container"></div>
</template>

<script>
import * as d3v4 from "d3v4";
import { round,getDataUrl } from '../../utils/functions';
import { loadFonts,copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import UserService from '../../services/user.service';

export default {
  name: 'PolarChart',
  props: {
    id:{
      type: String,
      required: true
    },
    size: {
      type: Number,
      required: false,
    },
    margin: {
      type: Object,
      required: false,
    },
    polarData: {
      type: Object,
      required: true,
    },
    dataPer90MinText: {
      type: String,
      required: false,
    },
    adjustToPossessionText: {
      type: String,
      required: false,
    },
    playerName: {
      type: String,
      required: true,
    },
    polarTitle: {
      type: String,
      required: true,
    },
    yob: {
      type: String,
      required: true,
    },
    mediaMode:{
      type: Boolean,
      default: false
    },
    colors:{
      type: Object,
      required:false
    },
    bgColor:{
      type: String,
      required:false
    },
    textColor:{
      type: String,
      required:false
    },
    axisColor:{
      type: String,
      required:false
    },
    logoCfg:{
      type: String,
      required:false
    },
    embed:{
      type: Boolean,
      default: false
    },
    fontFamily:{
      type: String,
      default: 'NeueMontreal'
    }
  },
  data() {
    return {
      observer: null,
      images: {}
    }
  },
  computed: {
    svgWidth(){
      const polarScale = d3.scale.linear()
      .range([0.86, 0.92])
      .domain([650, 1000]);
      let SVG_WIDTH = this.size * 0.85 > 350 || this.embed ? this.size * polarScale(this.size): 350;
      SVG_WIDTH = this.embed ? this.size : SVG_WIDTH;
      return SVG_WIDTH;
    },
    svgHeight() {
      const isMobileDevice = this.svgWidth < 440 ? 1 : 0;
      let SVG_HEIGHT = isMobileDevice ? this.svgWidth * 0.9: this.svgWidth * .75;
      SVG_HEIGHT = this.embed ? this.size * 0.8 : SVG_HEIGHT;
      return SVG_HEIGHT;
    },
    darkMode(){
      return this.$vuetify.theme.dark;
    },
    rootElementId(){
      return `polar${this.id}`
    }
  },
  async mounted() {
    this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
    var cfg = {
     levels: 4,				//How many levels or inner circles should there be drawn
     innerRadius: 10, //Radius of the arc close to the center
     opacityPetals: 0.7, //Opacity of petals (barAreas)
     opacityPetalsHover: 0.2, //Opacity of petals (barAreas) on hover
     paddleAnglePetal: 0.4, // the angle padding between petals
     colorBorder: "Black",  // color of the border of the bars
     colorBorderHover: "MediumBlue",  // color of the border of the bars during hover
     strokeWidth: 0.4, //The width of the stroke around each bar
     strokeWidthHover: 2, //The width of the stroke around each bar on hover
     labelFactor: this.$vuetify.breakpoint.xlOnly ? 1.15 : 1.2, 	//How much farther than the radius of the outer circle should the labels be placed
     wrapWidth: this.$vuetify.breakpoint.xlOnly ? 120 : 90, 		//The number of pixels after which a label needs to be given a new line
     opacityCircles: 0.1, 	//The opacity of the circles of each blob
     outlineStrokeWidth: 1,
     labelFontSize: 20, //Font size of the labels
     innerCircleFontSize: 16,		//Font size of the labels
     color: [this.colors.fillColor, this.colors.comparisonFillColor],	//Color function
     borderColor: [this.colors.borderColor, this.colors.comparisonBorderColor],	//Color function
     tooltipFontSize: 24,
     //Legend
     legendSquareLength: 33, //Legend squares size
     legendFontSize: 18, //Legend font size
    };

    const margin = this.margin ? this.margin : {top: 0, right: 0, bottom: 0, left: 0};
    const SVG_WIDTH = this.svgWidth;
    const isMobileDevice = SVG_WIDTH < 440 ? 1 : 0;
    const SVG_HEIGHT = this.svgHeight;
    const CHART_SIZE = SVG_WIDTH / 2;
    const CHART_SIZE_HEIGHT = SVG_HEIGHT / 2;
    const ratio = SVG_WIDTH <= 380 ? 0.002 : SVG_WIDTH < 700 ? 0.0017 : 0.00135;
    const CHART_SIZE_RATIO = CHART_SIZE * ratio; //CHART_SIZE
    //Over this value, valueToShow format to just one decimal
    const VALUE_OVER_WHICH_JUST_ONE_DECIMAL = 10;
    const barColorsByCategory = [{categoryId: 10, code: 'Passing', color:"#36D8AF"},
                    {categoryId: 11, code: 'Saving', color:"#FF9500"},
                    {categoryId: 12, code: 'Defending', color:"#FF0000"},
                    {categoryId: 13, code: 'Shooting', color:"#00B200"},
                    {categoryId: 14, code: 'BallHandling', color:"#FCE230"},
                    {categoryId: 15, code: 'Association', color:"#83D0F9"},
                    {categoryId: 16, code: 'Aerial', color:"#016AD8"},
                    {categoryId: 17, code: 'Physical', color:"#81AFF0"},
                    {categoryId: 60, code: 'Crosses', color:"#F17FDC"},
                    {categoryId: 61, code: 'Carries', color:"#4A4A4A"},
                    {categoryId: 63, code: 'Turnovers', color:"#B37878"},
                    {categoryId: 64, code: 'SequenceInvolvement', color:"#A45C44"},
                    {categoryId: 69, code: 'OutPlay', color:"#3498DB"},
                    {categoryId: 70, code: 'LineBreakingPasses', color:"#8E44AD"},
                    {categoryId: 71, code: 'PassUnderPressure', color:"#1ABC9C"},
                    {categoryId: 72, code: 'BallReceived', color:"#D35400"},
                    {categoryId: 73, code: 'OnBallPressure', color:"#2ECC71"},
                    {categoryId: 74, code: 'SpatialCoverage', color:"#C0392B"},
                    {categoryId: 75, code: 'OffBallPressure', color:"#"},
                    {categoryId: 77, code: 'Distance', color:"#FFD700"},
                    {categoryId: 78, code: 'Sprint', color:"#E53935"},
                    {categoryId: 79, code: 'Acceleration', color:"#FF9800"},
                    {categoryId: 80, code: 'Speed', color:"#2196F3"},
                    {categoryId: 90, code: 'LineBreakingCarries', color:"#C78BE0"},
                    {categoryId: 91, code: 'OnBallPressureReceived', color:"#1C7843"},
                    {categoryId: 92, code: 'LineBreakingActions', color:"#CA47FC"},
                    {categoryId: 93, code: 'OffBallRuns', color:"#FF5FA2"}];

    const polarWidth = SVG_WIDTH - margin.right * 2;
    //If this field increase, svg width increase (Don't affect to polar width)
    const POLAR_RADIUS = polarWidth > 230 || this.embed ? polarWidth : 230;

    var id = `#${this.rootElementId}`,
    //Order all stats by category
    playerPolarData = this.polarData.statDTOs[0],
    //Remove duplicated categories
    categoryIdsOnPolar = [...new Set(playerPolarData.map(s=> s.categoryId))],
    //Filter categories to show on legend sorted with the same order as polar
    categoriesOnLegend = barColorsByCategory.filter(c=> categoryIdsOnPolar.includes(c.categoryId))
    .sort((a, b) => categoryIdsOnPolar.indexOf(a.categoryId) - categoryIdsOnPolar.indexOf(b.categoryId)),
    //If the supplied maxValue is smaller than the actual one, replace by the max in the data
    maxValue = 1,
    allAxis = (playerPolarData.map((stat, index) => {
      let statValue = round(stat.valueToShow, stat.valueToShow > VALUE_OVER_WHICH_JUST_ONE_DECIMAL ? 1 : 2);
      //If value > 0
      if(statValue){
        statValue += stat.unitCode ? " " + stat.unitCode : "";
        statValue += stat.isPercentage ? "%" : "";
      }else if(stat.statTypeId == 3){
        //If value is 0 and is a Ratio Stat (statTypeId = 3), set - as value to show
        statValue = "-";
      }
      return {
        label: (stat.isAdjustedToPossession ? "*" : "") + this.$t('statsNaming.' + stat.code),
        value: statValue,
        tooltip: this.$t('statsNaming.description.' + stat.code)
      }
    })),
    total = allAxis.length,					//The number of different axes
    radius = Math.min(POLAR_RADIUS/2, POLAR_RADIUS/2), 	//Radius of the outermost circle
    maxRadiusAllowed = radius - 4, //Max radius of the petals (barArea)
    angleSlice = Math.PI * 2 / total, //The width in radians of each "slice"
    minMaxValues = (playerPolarData.map(function(stat, index){ return {min: stat.minValue, max: stat.maxValue};})),	//Min and Max Values of each stat
    //Scale for the radius
    rScale = d3.scale.linear()
      .range([0, radius])
      .domain([0, maxValue]),
    radiansOffsetAxis = Math.PI/total;


    ///////////////////////////////////////////////////////////////////
    //////////// Create the container SVG, g and tooltip /////////////
    /////////////////////////////////////////////////////////////////

    //Remove whatever chart with the same id/class was present before
    d3.select(id).select("svg").remove();

    //Initiate the polar chart SVG
    var svg = d3.select(id).append("svg")
        .attr("width",  SVG_WIDTH)
        .attr("height", SVG_HEIGHT)
        .style("fill", this.textColor)
        .style("background", this.bgColor)
        .attr("id",`${this.rootElementId}SVG`)
        .attr("class", "polar");

    //Append a g element
    var g = svg.append("g").attr("transform", "translate(" + (CHART_SIZE) + "," + CHART_SIZE_HEIGHT * (isMobileDevice ? 1 : 1.05 )+ ")");

    // Define the div for the tooltip
    var tooltip = d3v4.select("body").append("div")
    .attr("class", "tooltip no-arrow")
    .style("z-index", "9999")
    .style("font-size", (cfg.tooltipFontSize * CHART_SIZE_RATIO) + "px")
    .style("display", "none");

    ////////////////////////////////////
    ////////// Glow filter  ///////////
    //////////////////////////////////

    //Filter for the outside glow
    var filter = g.append('defs').append('filter').attr('id','glow'),
      feGaussianBlur = filter.append('feGaussianBlur').attr('stdDeviation', 2.5 * CHART_SIZE_RATIO).attr('result','coloredBlur'),
      feMerge = filter.append('feMerge'),
      feMergeNode_1 = feMerge.append('feMergeNode').attr('in','coloredBlur'),
      feMergeNode_2 = feMerge.append('feMergeNode').attr('in','SourceGraphic');

    /////////////////////////////////////////////////////////
    /////////////// Draw the Circular grid //////////////////
    /////////////////////////////////////////////////////////

    //Wrapper for the grid & axes
    var axisGrid = g.append("g").attr("class", "axisWrapper");

    //Draw the background circles
    axisGrid.selectAll(".levels")
      .data(d3.range(1,(cfg.levels+1)).reverse())
      .enter()
      .append("circle")
      .attr("class", "gridCircle")
      .attr("r", function(d, i){ return radius/cfg.levels * d;})
      .style("fill", "#CDCDCD")
      .style("stroke", function(d,i) { return i % 2 === 0? "#9C9C9C" : "#CDCDCD" })
      .style("stroke-width", cfg.outlineStrokeWidth * CHART_SIZE_RATIO)
      .style("fill-opacity", cfg.opacityCircles)
      .style("filter" , "url(#glow)");

    // TITLES
    //Year of birth
    svg.append("text")
      .attr("x", SVG_WIDTH / 2)
      .attr("y", isMobileDevice ? 17 * CHART_SIZE_RATIO : 25 * CHART_SIZE_RATIO)
      .attr("text-anchor", "middle")
      .style("font-size", (32 * CHART_SIZE_RATIO) + "px")
      .style("font-family", this.fontFamily)
      .style("font-weight", "bold")
      .style("color", this.textColor)
      .style("fill",'currentColor')
      .text(this.playerName)
      .append("tspan")
      .attr("dx", 0)
      .attr("dy", -(2 * CHART_SIZE_RATIO))
      .style("font-size", (24 * CHART_SIZE_RATIO) + "px")
      .style("font-weight", "normal")
      .text(this.yob ? " ('" + this.yob + ")" : ""); //Last 2 digits of Player's Year of Birth

    //Write Team and Season (or Seasons) text
    svg.append("text")
      .attr("x", SVG_WIDTH / 2)
      .attr("y", isMobileDevice ? 40 * CHART_SIZE_RATIO : 55 * CHART_SIZE_RATIO)
      .attr("text-anchor", "middle")
      .style("font-size", (24 * CHART_SIZE_RATIO) + "px")
      .style("font-family", this.fontFamily)
      .style("color",this.textColor)
      .style("fill",'currentColor')
      .html(this.polarTitle + " · " + this.polarData.minutesPlayed + " mins.");

    // Draw Left Text
    svg.append("text")
      .attr("x", 0)
      .attr("y", SVG_HEIGHT - 70 * CHART_SIZE_RATIO)
      .attr("text-anchor", "start")
      .attr("dy", "0.8em")
      .style("font-size", (24 * CHART_SIZE_RATIO) + "px")
      .style("font-family", this.fontFamily)
      .style("color",this.textColor)
      .style("fill",'currentColor')
      .text(this.dataPer90MinText);

    svg.append("text")
      .attr("x", 0)
      .attr("y", SVG_HEIGHT - 35 * CHART_SIZE_RATIO)
      .attr("text-anchor", "start")
      .attr("dy", "0.8em")
      .style("font-size", (24 * CHART_SIZE_RATIO) + "px")
      .style("font-family", this.fontFamily)
      .style("color", this.textColor)
      .style("fill",'currentColor')
      .text(this.adjustToPossessionText);

    // Draw Driblab Logo
    const logoWidth = SVG_WIDTH * 0.22;
    const logoHeight = SVG_HEIGHT * 0.1;

    if(!this.mediaMode){
      let base_image = new Image();
      base_image.src = this.mediaMode ? require(this.logoCfg === 'white' ? "@/assets/img/driblab_normal_white.png" : "@/assets/img/driblab_normal_color.png") :
       require((this.logoCfg === 'white' || this.darkMode) ? "@/assets/img/driblab_white.png" : "@/assets/img/driblab_color.png");
      base_image.onload = (event) => {
        const dataUrl = getDataUrl(event.currentTarget);
        this.drawImage(
        svg,
        "driblab-logo",
        SVG_WIDTH - logoWidth,
        SVG_HEIGHT - logoHeight,
        logoWidth,
        logoHeight,
        dataUrl,
        `${this.mediaMode}|${this.logoCfg}`
        );
      }
    }


    //////////////////////////////////////////////////////////
   /////                Draw the axes                   /////
  //////////////////////////////////////////////////////////

    //Create the straight lines radiating outward from the center
    var axis = axisGrid.selectAll(".axis")
      .data(allAxis)
      .enter()
      .append("g")
      .attr("class", "axis");
    //Append the lines
    axis.append("line")
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", function(d, i){ return rScale(maxValue) * Math.cos(angleSlice*i - Math.PI/2); })
      .attr("y2", function(d, i){ return rScale(maxValue) * Math.sin(angleSlice*i - Math.PI/2); })
      .attr("class", "line")
      .style("stroke", this.axisColor)
      .style("stroke-width", (2 * CHART_SIZE_RATIO) + "px");

    var values = axisGrid.selectAll(".maxValues")
      .data(minMaxValues)
      .enter()
      .append("g")
      .attr("class", "maxValues");
    values.append("text")
        .attr("class", "axisLabel")
        .style("font-size", (CHART_SIZE_RATIO * cfg.innerCircleFontSize) - (isMobileDevice * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("color", this.textColor)
        .style("fill",'currentColor')
        .attr("text-anchor", "middle")
        .attr("dy", "0.6em")
        .attr("x", function(d, i){ return 0.25 * rScale(maxValue * 0.84 * cfg.labelFactor) * Math.cos(angleSlice*i- Math.PI/2); })
        .attr("y", function(d, i){ return 0.25 * rScale(maxValue * 0.84 * cfg.labelFactor) * Math.sin(angleSlice*i- Math.PI/2); })
        .text(function(d){ return d.max > 5 ? round(d.min + (0.25 * (d.max - d.min)), 1) : round(d.min + (0.25 * (d.max - d.min)),2)})
        .call(this.wrap, cfg.wrapWidth);
     values.append("text")
         .attr("class", "axisLabel")
         .style("font-size", (CHART_SIZE_RATIO * cfg.innerCircleFontSize) - (isMobileDevice * CHART_SIZE_RATIO) + "px")
         .style("font-family", this.fontFamily)
         .style("color", this.textColor)
         .style("fill",'currentColor')
         .attr("text-anchor", "middle")
         .attr("dy", "0.6em")
         .attr("x", function(d, i){ return 0.5 * rScale(maxValue * 0.84 * cfg.labelFactor) * Math.cos(angleSlice*i- Math.PI/2); })
         .attr("y", function(d, i){ return 0.5 * rScale(maxValue * 0.84 * cfg.labelFactor) * Math.sin(angleSlice*i- Math.PI/2); })
         .text(function(d){ return d.max > 5 ? round(d.min + (0.5 * (d.max - d.min)),1) : round(d.min + (0.5 * (d.max - d.min)),2)})
         .call(this.wrap, cfg.wrapWidth);
     values.append("text")
         .attr("class", "axisLabel")
         .style("font-size", (CHART_SIZE_RATIO * cfg.innerCircleFontSize) - (isMobileDevice * CHART_SIZE_RATIO) + "px")
         .style("font-family", this.fontFamily)
         .style("color", this.textColor)
         .style("fill",'currentColor')
         .attr("text-anchor", "middle")
         .attr("dy", "0.6em")
         .attr("x", function(d, i){ return 0.75 * rScale(maxValue * 0.84 * cfg.labelFactor) * Math.cos(angleSlice*i- Math.PI/2); })
         .attr("y", function(d, i){ return 0.75 * rScale(maxValue * 0.84 * cfg.labelFactor) * Math.sin(angleSlice*i- Math.PI/2); })
         .text(function(d){ return d.max > 5 ? round(d.min + (0.75 * (d.max - d.min)),1) : round(d.min + (0.75 * (d.max - d.min)),2)})
         .call(this.wrap, cfg.wrapWidth);
      values.append("text")
        .attr("class", "axisLabel")
        .style("font-size", (CHART_SIZE_RATIO * cfg.innerCircleFontSize) - (isMobileDevice * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("color", this.textColor)
        .style("fill",'currentColor')
        .attr("text-anchor", "middle")
        .attr("dy", "0.6em")
        .attr("x", function(d, i){ return rScale(maxValue * 0.84 * cfg.labelFactor) * Math.cos(angleSlice*i- Math.PI/2); })
        .attr("y", function(d, i){ return rScale(maxValue * 0.84 * cfg.labelFactor) * Math.sin(angleSlice*i- Math.PI/2); })
        .text(function(d){ return d.max > 5 ? round(d.max, 1) : round(d.max, 2)})
        .call(this.wrap, cfg.wrapWidth);

      ///////////////////////////////////////////////////////////
     //////////////   Draw the polar areas      ////////////////
    ///////////////////////////////////////////////////////////
    var barArea = axisGrid.selectAll('.barArea')
      .attr('class', 'barArea')
      .data(playerPolarData)
      .enter().append('g');

    // Add bars
    var bar = barArea.append("path")
      .attr('class', 'bar')
      .attr("fill", function(d, i){
        let category = categoriesOnLegend.find(bc => bc.categoryId === d.categoryId);
        return category ? category.color : "#000000";
      })
      .attr("opacity", cfg.opacityPetals)
      .attr("d", d3v4.arc()
          .innerRadius(function(d) { return cfg.innerRadius; })
          .outerRadius(function(d) {
            //Calculate a min value to show (If value is close to min, path is too much small)
            let minValueToShow = d.minValue + (d.maxValue - d.minValue)/8;
            let valueToScale = d.valueToShow;
            //If value is under the min, show minValueToShow
            if((!d.isReverse && d.valueToShow < minValueToShow) || (d.isReverse && d.valueToShow > minValueToShow)){
              valueToScale = minValueToShow;
            }
            let radiusToReturn = rScale((valueToScale - d.minValue) / (d.maxValue - d.minValue));
            return radiusToReturn > maxRadiusAllowed ? maxRadiusAllowed : radiusToReturn;
          })
          .startAngle(function(d,i) { return i * angleSlice - radiansOffsetAxis;})
          .endAngle(function(d,i) { return i * angleSlice + angleSlice - radiansOffsetAxis;})
          .padAngle(cfg.paddleAnglePetal)
          .padRadius(cfg.innerRadius))
      .style("stroke", cfg.colorBorder)
      .style("stroke-width", cfg.strokeWidth);

    var self = this;
    // Add Tooltip on bar areas
    bar.on("mouseover", function(d){
          d3.select(this)
            .style("stroke", cfg.colorBorderHover)
            .style("stroke-width", cfg.strokeWidthHover)
            .style("opacity", cfg.opacityPetalsHover);
    })
    .on("mousemove", function(d){
      tooltip
        .style("left", d3.event.pageX + "px")
        .style("top", d3.event.pageY + "px")
        .style("display", "inline-block")
        .style("width", "auto")
        .style("height", "auto")
        .html("<b>" + self.$t('statsNaming.' + d.code) + "</b><br/>" + self.$t('statsNaming.description.' + d.code));
    })
    .on("mouseout", function(d){
      tooltip
        .style("display", "none");
      d3.select(this)
        .style("fill", function() { return d.color; })
        .style("stroke-width", cfg.strokeWidth)
        .style("stroke", cfg.colorBorder)
        .style("opacity", cfg.opacityPetals);
    });

      ///////////////////////////////////////////////////////////
     //////////////       Draw labels       ////////////////////
    ///////////////////////////////////////////////////////////
    axis.append("text")
      .style("color", this.textColor)
      .style("fill",'currentColor')
      .style("font-size", (CHART_SIZE_RATIO * cfg.labelFontSize) + "px")
      .style("font-family", this.fontFamily)
      .attr("text-anchor", "middle")
      .attr("dy", "0.6em")
      .attr("x", function(d, i){
        var coefX = isMobileDevice ? 1.09 : 1.13;
        return rScale(maxValue * coefX * cfg.labelFactor) * Math.cos(angleSlice * i - Math.PI/2);
      })
      .attr("y", function(d, i){
        var coefY = 0.93;
        if (i === 0) // top label
          coefY = d.label.length > 15 ? 1 : 0.96;
        else if (i === 1 || i === total - 1 && (d.label.length + d.value.length) > 15) // labels next to top label, a bit up
          coefY = 1.01;
        else if (total % 2 === 0 && i === total/2) // bottom label
          coefY = 0.9;
        return (rScale(maxValue * coefY * cfg.labelFactor) * Math.sin(angleSlice*i - Math.PI/2))
       })
       .text(function(d){ return d.label })
       .on('mouseover', function(stat, i) {
          const statName = stat.label;
          let widthTooltip = 350;
          let lines = Math.ceil(self.measureText(stat.tooltip) / widthTooltip);

          let heightTooltip = (CHART_SIZE * 0.2 + (isMobileDevice * 2.5)) + (lines * 10);
          if(((widthTooltip / 2) + this.getBoundingClientRect().left) >= window.innerWidth){
            widthTooltip = (window.innerWidth - this.getBoundingClientRect().left) * 1.75;
          }else if( (this.getBoundingClientRect().left - (widthTooltip / 2))  <= 0){
            widthTooltip = this.getBoundingClientRect().left * 1.75;
          }

          let tooltipY = event.pageY - (heightTooltip + (isMobileDevice * 5));

          if(tooltipY < 0){
            d3.select("body > .tooltip").attr("class",'tooltip no-arrow');
            tooltipY = event.pageY + 8;
          }else{
            d3.select("body > .tooltip").attr("class",'tooltip');
          }

          d3.select("body > .tooltip")
            .transition()
            .duration(200)
            .style("display", "inline-block")
            .style("opacity", 0.95)
            .style("width", widthTooltip + "px")
            .style("height", "auto");

          d3.select("body > .tooltip").html("<b>" + statName + "</b></br>" + stat.tooltip)
            .style("font-size", (cfg.tooltipFontSize * CHART_SIZE_RATIO) + "px")
            .style("padding-top", (10 * CHART_SIZE_RATIO) + "px")
            .style("left", this.getBoundingClientRect().right - (this.getBoundingClientRect().width/2) - widthTooltip/2 + "px")
            .style("top", tooltipY + "px");

        })
        .on('mouseout',function() {
          d3.select("body > .tooltip").transition()
            .duration(300)
            .style("display", "none");
        })
       .call(this.wrap,  isMobileDevice ? 0.6 * cfg.wrapWidth : cfg.wrapWidth)
       .append("tspan")
       .attr("x", function(d, i){
          var coefX = isMobileDevice ? 1.09 : 1.13;
          return rScale(maxValue * coefX * cfg.labelFactor) * Math.cos(angleSlice * i - Math.PI/2);
       })
       .attr("dy", "1.3em")
       .attr("fill", this.darkMode ? cfg.color[0] : cfg.borderColor[0])
       .style("font-weight", "bold")
       .text(function(d){ return d.value });


      ///////////////////////////////////////////////////////////
     //////////////       Draw Legend       ////////////////////
    ///////////////////////////////////////////////////////////
    var legend = svg.append("g")
       .attr("font-family", this.fontFamily)
       .attr("font-size", (cfg.legendFontSize * CHART_SIZE_RATIO))
       .selectAll("g")
       .data(categoriesOnLegend)
       .enter()
       .append("g")
        .attr("transform", function(d, i) { return "translate(0," + ((isMobileDevice ? 17 * CHART_SIZE_RATIO : 25 * CHART_SIZE_RATIO)
        + (i * cfg.legendSquareLength * CHART_SIZE_RATIO)) + ")"; });

    //Set legend squares
    legend.append("rect")
       .attr("x", 0)
       .attr("width", (cfg.legendSquareLength * CHART_SIZE_RATIO))
       .attr("height", (cfg.legendSquareLength * CHART_SIZE_RATIO))
       .attr("fill", (d) => { return d.color; })
       .attr('stroke', '#000')
       .attr('stroke-width', (cfg.strokeWidth * CHART_SIZE_RATIO));

    //Set legend labels (stats)
    legend.append("text")
       .attr("x", ((cfg.legendSquareLength + 7) * CHART_SIZE_RATIO))
       .attr("y", 9.5)
       .attr("dy", "0.32em")
       .style("fill", this.darkMode ? "#FFF" : "#000")
       .text((d) => { return this.$t('faq.metrics.categories.' + d.code); });

    if(svg.node()){
      let font = await loadFonts(this.fontFamily);
      copyAndAppendElement(svg.node(),font.node(),0,0);
    }
  },
  methods: {
    drawImage: function(svg, idLogo, x, y, width, height, img, id) {
      if(!this.images[id]){
        let imgNode = svg.append("svg:image")
          .attr("id", idLogo)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
        this.images[id] = imgNode.node().cloneNode(true);
      }else{
        svg.node().appendChild(this.images[id]);
      }
    },
    /////////////////////////////////////////////////////////
  	/////////////////// Helper Function /////////////////////
  	/////////////////////////////////////////////////////////

  	//Taken from http://bl.ocks.org/mbostock/7555321
  	//Wraps SVG text
  	wrap(text, width) {
  	  text.each(function() {
    		var text = d3.select(this),
    			words = text.text().split(/\s+/).reverse(),
    			word,
    			line = [],
    			lineNumber = 0,
    			lineHeight = 1.2, // ems
    			y = text.attr("y"),
    			x = text.attr("x"),
    			dy = parseFloat(text.attr("dy")),
    			tspan = text.text(null).append("tspan").attr("x", x).attr("y", y).attr("dy", dy + "em");

    		while (word = words.pop()) {
    		  line.push(word);
    		  tspan.text(line.join(" "));
    		  if (tspan.node().getComputedTextLength() > width) {
    				line.pop();
    				tspan.text(line.join(" "));
    				line = [word];
    				tspan = text.append("tspan").attr("x", x).attr("y", y).attr("dy", ++lineNumber * lineHeight + dy + "em").text(word);
    		  }
    		}
  	  });
  	},//wrap
    /* This function returns the size in pixels of the string */
  	measureText: function(string, fontSize = 10) {
  	  const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
  			0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
  			0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
  			0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
  			0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
  			0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
  			0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
  			0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
  	  const avg = 0.5293256578947368;

  	  return string
  	    .split('')
  	    .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
  	    .reduce((cur, acc) => acc + cur) * fontSize;
  	},
    onResize(){
      let elto = document.getElementById(this.rootElementId);
      if(elto){
        this.$emit("sizeChange", {height:elto.clientHeight, width:elto.clientWidth});
      }
    }
  },
  destroyed() {
    d3.select(`#${this.rootElementId}`).select("svg").remove();
    delete this.observer;
  },
}

</script>
<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/views/base/PolarChart.vue